import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getCallLog, fetchCallTimeline } from "../callLogApi";
import { formatCallStatus } from "../utils/callStatusLabels";
import {
  ArrowLeft,
  Phone,
  PhoneCall,
  MapPin,
  Calendar,
  User,
  Building2,
  CheckCircle2,
  Clock,
  Timer,
  History,
  MessageSquare,
  LinkIcon,
} from "lucide-react";
import "../css/calllog/calllogdetail.css";

// Format duration (stored in minutes) → "5 min" / "5 min 30 sec" / "45 sec"
function formatDuration(mins) {
  if (mins == null || mins === "") return null;
  const num = Number(mins);
  if (isNaN(num) || num <= 0) return null;
  if (num < 1) return `${Math.round(num * 60)} sec`;
  const m = Math.floor(num);
  const s = Math.round((num - m) * 60);
  return s ? `${m} min ${s} sec` : `${m} min`;
}

export default function CallLogDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState([]);
  const [tlLoading, setTlLoading] = useState(false);

  useEffect(() => {
    getCallLog(id)
      .then(res => {
        setLog(res.data);
        setLoading(false);
        const contact = res.data?.contactNumber;
        if (contact) {
          setTlLoading(true);
          fetchCallTimeline(contact)
            .then(r => setTimeline(Array.isArray(r.data) ? r.data : []))
            .catch(() => setTimeline([]))
            .finally(() => setTlLoading(false));
        }
      })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="cld-loading">
        <div className="cld-spinner" />
        <p>Loading call details…</p>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="cld-error">
        <p>Call log not found</p>
        <button onClick={() => navigate(-1)} className="cld-back-btn">
          Go Back
        </button>
      </div>
    );
  }

  // Status badge styling
  const getStatusColor = (status) => {
    if (!status) return "status-pending";
    if (status.toLowerCase() === "interested") return "status-interested";
    if (status.toLowerCase() === "not interested") return "status-not-interested";
    if (status.toLowerCase() === "follow-up") return "status-followup";
    return "status-pending";
  };

  const getVisitColor = (visit) => {
    if (!visit) return "visit-pending";
    if (visit.toLowerCase() === "pending") return "visit-pending";
    if (visit.toLowerCase() === "completed") return "visit-completed";
    if (visit.toLowerCase() === "scheduled") return "visit-scheduled";
    return "visit-pending";
  };

  return (
    <div className="cld-page">
      {/* Back Button */}
      <button className="cld-back-btn-modern" onClick={() => navigate(-1)}>
        <ArrowLeft size={18} />
      </button>

      {/* Hero Section */}
      <div className="cld-hero">
        <div className="cld-hero-content">
          <h1 className="cld-title">{log.clientName}</h1>
          <p className="cld-subtitle">Call Log Details</p>
          <div className="cld-badges">
            <span className={`cld-badge ${getStatusColor(log.currentStatus)}`}>
              {log.currentStatus ? formatCallStatus(log.currentStatus) : "Unknown"}
            </span>
            <span className={`cld-badge ${getVisitColor(log.visit)}`}>
              {log.visit || "Pending"}
            </span>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="cld-content">
        {/* Client Information Card */}
        <div className="cld-card">
          <h3 className="cld-section-title">Client Information</h3>
          <div className="cld-grid-2">
            <div className="cld-info-item">
              <User size={18} className="cld-icon" />
              <div>
                <p className="cld-label">Name</p>
                <p className="cld-value">{log.clientName}</p>
              </div>
            </div>
            <div className="cld-info-item">
              <Phone size={18} className="cld-icon" />
              <div>
                <p className="cld-label">Contact Number</p>
                <p className="cld-value">{log.contactNumber}</p>
              </div>
            </div>
            <div className="cld-info-item">
              <MapPin size={18} className="cld-icon" />
              <div>
                <p className="cld-label">Address</p>
                <p className="cld-value">{log.address || "—"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call Information Card */}
        <div className="cld-card">
          <h3 className="cld-section-title">Call Information</h3>
          <div className="cld-grid-2">
            <div className="cld-info-item">
              <Calendar size={18} className="cld-icon" />
              <div>
                <p className="cld-label">Call Date & Time</p>
                <p className="cld-value">{new Date(log.callDate).toLocaleString()}</p>
              </div>
            </div>
            <div className="cld-info-item">
              <Clock size={18} className="cld-icon" />
              <div>
                <p className="cld-label">Call Status</p>
                <p className="cld-value">{log.currentStatus ? formatCallStatus(log.currentStatus) : "—"}</p>
              </div>
            </div>
            <div className="cld-info-item">
              <Timer size={18} className="cld-icon" />
              <div>
                <p className="cld-label">Call Duration</p>
                <p className="cld-value">{formatDuration(log.callDuration) || "—"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call Timeline Card — mobile-style call history for this contact */}
        <div className="cld-card cld-timeline-card">
          <h3 className="cld-section-title">
            <History size={18} /> Call Timeline
            <span className="cld-tl-count">{timeline.length} call{timeline.length !== 1 ? "s" : ""}</span>
          </h3>

          {tlLoading ? (
            <p className="cld-tl-empty">Loading call history…</p>
          ) : timeline.length === 0 ? (
            <p className="cld-tl-empty">No call history for {log.contactNumber}.</p>
          ) : (
            <>
              {/* First / Last summary */}
              <div className="cld-tl-summary">
                <div className="cld-tl-summary-item">
                  <span className="cld-tl-summary-label">First Call</span>
                  <span className="cld-tl-summary-value">
                    {new Date(timeline[0].callDate).toLocaleString()}
                  </span>
                </div>
                <div className="cld-tl-summary-item">
                  <span className="cld-tl-summary-label">Last Call</span>
                  <span className="cld-tl-summary-value">
                    {new Date(timeline[timeline.length - 1].callDate).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Chronological list (oldest → newest) */}
              <ul className="cld-timeline">
                {timeline.map((entry, i) => {
                  const isFirst = i === 0;
                  const isLast  = i === timeline.length - 1;
                  const isCurrent = entry._id === log._id;
                  const duration = formatDuration(entry.callDuration);
                  return (
                    <li
                      key={entry._id}
                      className={`cld-tl-item ${isCurrent ? "current" : ""}`}
                    >
                      <div className="cld-tl-marker">
                        <span className={`cld-tl-dot ${getStatusColor(entry.currentStatus)}`}>
                          <PhoneCall size={12} />
                        </span>
                        {!isLast && <span className="cld-tl-line" />}
                      </div>

                      <div className="cld-tl-content">
                        <div className="cld-tl-row">
                          <span className="cld-tl-time">
                            <Clock size={13} /> {new Date(entry.callDate).toLocaleString()}
                          </span>
                          {isFirst && <span className="cld-tl-badge first">First</span>}
                          {isLast  && <span className="cld-tl-badge last">Last</span>}
                          {isCurrent && <span className="cld-tl-badge current-badge">This call</span>}
                        </div>

                        <div className="cld-tl-row">
                          <span className={`cld-badge ${getStatusColor(entry.currentStatus)}`}>
                            {entry.currentStatus ? formatCallStatus(entry.currentStatus) : "Unknown"}
                          </span>
                          {duration && (
                            <span className="cld-tl-duration">
                              <Timer size={13} /> {duration}
                            </span>
                          )}
                          {entry.createdByName && (
                            <span className="cld-tl-agent">
                              <User size={12} /> {entry.createdByName}
                            </span>
                          )}
                        </div>

                        {entry.remarks && (
                          <p className="cld-tl-remark">{entry.remarks}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* Property Information Card */}
        <div className="cld-card">
          <h3 className="cld-section-title">Property Information</h3>
          <div className="cld-grid-2">
            <div className="cld-info-item">
              <Building2 size={18} className="cld-icon" />
              <div>
                <p className="cld-label">Property Discussed</p>
                <p className="cld-value">{log.propertyDetails || "—"}</p>
              </div>
            </div>
            <div className="cld-info-item">
              <MapPin size={18} className="cld-icon" />
              <div>
                <p className="cld-label">Property Address</p>
                <p className="cld-value">{log.propertyAddress || "—"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Visit & Meeting Card */}
        <div className="cld-card">
          <h3 className="cld-section-title">Visit & Follow-up</h3>
          <div className="cld-grid-2">
            <div className="cld-info-item">
              <CheckCircle2 size={18} className="cld-icon" />
              <div>
                <p className="cld-label">Visit Status</p>
                <p className="cld-value">{log.visit || "—"}</p>
              </div>
            </div>
            <div className="cld-info-item">
              <Calendar size={18} className="cld-icon" />
              <div>
                <p className="cld-label">Meeting Scheduled</p>
                <p className="cld-value">
                  {log.meetingScheduled ? new Date(log.meetingScheduled).toLocaleString() : "—"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Remarks Card */}
        {log.remarks && (
          <div className="cld-card">
            <h3 className="cld-section-title">
              <MessageSquare size={18} /> Remarks
            </h3>
            <p className="cld-remarks-text">{log.remarks}</p>
          </div>
        )}

        {/* Linked Records Card */}
        {(log.lead || log.property || log.deal) && (
          <div className="cld-card">
            <h3 className="cld-section-title">
              <LinkIcon size={18} /> Linked Records
            </h3>
            <div className="cld-links">
              {log.lead && (
                <div className="cld-link-item">
                  <span className="cld-link-label">Lead:</span>
                  <span className="cld-link-value">{log.lead.full_name}</span>
                </div>
              )}
              {log.property && (
                <div className="cld-link-item">
                  <span className="cld-link-label">Property:</span>
                  <span className="cld-link-value">{log.property.propertyName}</span>
                </div>
              )}
              {log.deal && (
                <div className="cld-link-item">
                  <span className="cld-link-label">Deal:</span>
                  <span className="cld-link-value">{log.deal.dealName}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Agent Information Card */}
        <div className="cld-card">
          <h3 className="cld-section-title">Agent Information</h3>
          <div className="cld-info-item">
            <User size={18} className="cld-icon" />
            <div>
              <p className="cld-label">Agent</p>
              <p className="cld-value">{log.createdByName || "Admin"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}